export type SttCallbacks = {
  // localId identyfikuje jedną wypowiedź (od pierwszego partial do final)
  onPartial?: (text: string, localId?: string) => void;
  onFinal?: (text: string, localId?: string) => void;
  onError?: (err: Error) => void;
  onStart?: () => void;
  onEnd?: () => void;
};

export type SttHandle = {
  stop: () => void;
  isRunning: () => boolean;
};

type SpeechRecognitionType = typeof window extends any ? (any) : never;

function getRecognitionCtor(): any | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function isSttSupported(): boolean {
  return !!getRecognitionCtor();
}

export function startStt(
  lang: string,
  callbacks: SttCallbacks,
  opts?: { continuous?: boolean; interimResults?: boolean; prewarmDeviceId?: string; autoRestart?: boolean }
): SttHandle {
  const Recognition = getRecognitionCtor();
  if (!Recognition) {
    const err = new Error('Rozpoznawanie mowy nie jest wspierane w tej przeglądarce');
    callbacks.onError && callbacks.onError(err);
    return {
      stop: () => {},
      isRunning: () => false
    };
  }
  const recognition = new Recognition();
  recognition.lang = lang || 'pl-PL';
  recognition.continuous = opts?.continuous ?? true;
  recognition.interimResults = opts?.interimResults ?? true;
  recognition.maxAlternatives = 1;

  let running = false;
  let currentLocalId: string | null = null;
  const newLocalId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  let forceStop = false;
  const shouldAutoRestart = opts?.autoRestart !== false; // domyślnie włączone
  let restartTimer: any = null;
  const scheduleRestart = (delayMs: number) => {
    if (forceStop || !shouldAutoRestart) return;
    if (restartTimer) return;
    restartTimer = setTimeout(() => {
      restartTimer = null;
      try { recognition.start(); } catch {}
    }, Math.max(200, delayMs));
  };

  recognition.onstart = () => {
    running = true;
    if (restartTimer) { try { clearTimeout(restartTimer); } catch {} restartTimer = null; }
    callbacks.onStart && callbacks.onStart();
  };
  recognition.onend = () => {
    running = false;
    // Jeżeli nie zatrzymano ręcznie – zaplanuj wznowienie (unikamy duplikacji restartu)
    if (!forceStop && shouldAutoRestart) scheduleRestart(450);
    else callbacks.onEnd && callbacks.onEnd();
  };
  recognition.onerror = (event: any) => {
    callbacks.onError && callbacks.onError(new Error(event?.error || 'błąd STT'));
    // Auto-restart przy typowych błędach, jeśli nie zatrzymano ręcznie
    const code = String(event?.error || '');
    if (!forceStop && shouldAutoRestart) {
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        // brak zgody – nie restartujemy automatycznie
        return;
      }
      if (code === 'no-speech') scheduleRestart(500);
      else if (code === 'aborted') scheduleRestart(800);
      else if (code === 'audio-capture') scheduleRestart(900);
      else if (code === 'network') scheduleRestart(1000);
    }
  };
  recognition.onresult = (event: any) => {
    try {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const text = (res[0] && res[0].transcript) ? String(res[0].transcript) : '';
        const isFinal = !!res.isFinal;
        if (!text) continue;
        if (!currentLocalId) currentLocalId = newLocalId();
        if (isFinal) {
          callbacks.onFinal && callbacks.onFinal(text, currentLocalId);
          currentLocalId = null; // zakończ tę wypowiedź
        } else {
          callbacks.onPartial && callbacks.onPartial(text, currentLocalId);
        }
      }
    } catch (e) {
      callbacks.onError && callbacks.onError(e as Error);
    }
  };

  // Opcjonalnie „pregrzanie” wybranego urządzenia, by wymusić dostęp do konkretnego mikrofonu
  const mediaRef: { stream: MediaStream | null } = { stream: null };
  try {
    if (opts?.prewarmDeviceId && navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: opts.prewarmDeviceId } as any,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } as any
      })
        .then((stream) => { mediaRef.stream = stream; })
        .catch(() => { /* ignoruj */ });
    }
  } catch { /* ignore */ }

  try {
    recognition.start();
  } catch (e) {
    callbacks.onError && callbacks.onError(e as Error);
  }

  return {
    stop: () => {
      forceStop = true;
      try { recognition.stop(); } catch {}
      try { mediaRef.stream?.getTracks().forEach(t => t.stop()); mediaRef.stream = null; } catch {}
      running = false;
    },
    isRunning: () => running
  };
}


